import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BarChart3, MapPin, Ruler, TrendingUp, ArrowRight } from "lucide-react";
import { useCompare } from "../lib/compare";

const CURR_LABEL = { INR: "₹", USD: "$", AED: "AED " };
function fmtPrice(inr, curr = "INR") {
  const fx = curr === "USD" ? 1 / 84 : curr === "AED" ? 1 / 22.9 : 1;
  const v = inr * fx;
  if (v >= 10000000 && curr === "INR") return `${CURR_LABEL[curr]}${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 1000000) return `${CURR_LABEL[curr]}${(v / 1000000).toFixed(2)}M`;
  return `${CURR_LABEL[curr]}${v.toFixed(0)}`;
}

export default function CompareBar({ currency = "INR" }) {
  const { selected, clear, toggle } = useCompare();
  const [showModal, setShowModal] = useState(false);

  if (selected.length === 0) return null;

  const rows = [
    { label: "Price", key: "price_inr", fmt: (v) => fmtPrice(v, currency) },
    { label: "Area", key: "area_sqft", fmt: (v) => `${v.toLocaleString()} sqft` },
    { label: "Yield", key: "yield_pct", fmt: (v) => v ? `${v}%` : "—" },
    { label: "Location", key: "location", fmt: (v) => v },
    { label: "Type", key: "type", fmt: (v) => v },
    { label: "Status", key: "status", fmt: (v) => v },
    { label: "Tags", key: "tags", fmt: (v) => v?.join(", ") || "—" },
  ];

  return (
    <>
      {/* Floating Bar */}
      <AnimatePresence>
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-[90] bg-[#0E2A4E] border-t border-[#D4AF37]/30 shadow-2xl"
          data-testid="compare-bar"
        >
          <div className="max-w-[1440px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <BarChart3 size={18} className="text-[#D4AF37]" />
              <span className="text-[12px] tracking-[2px] uppercase">
                {selected.length} of 3 selected
              </span>
              <div className="flex gap-2 ml-2">
                {selected.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 bg-[#0A2342] border border-[#EDE6D3]/15 px-3 py-1.5">
                    <span className="text-[11px] tracking-[1px] text-[#EDE6D3]/80 max-w-[140px] truncate">{p.title}</span>
                    <button onClick={() => toggle(p)} className="text-[#EDE6D3]/40 hover:text-[#EDE6D3]">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={clear} className="text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50 hover:text-[#EDE6D3]" data-testid="compare-clear">
                Clear
              </button>
              <button
                onClick={() => setShowModal(true)}
                disabled={selected.length < 2}
                className="btn-primary text-[11px] disabled:opacity-30"
                data-testid="compare-open"
              >
                Compare <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Comparison Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-[#0A2342]/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-16 px-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              className="bg-[#0E2A4E] border border-[#EDE6D3]/15 w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="compare-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-[#EDE6D3]/12">
                <div>
                  <div className="eyebrow text-[#D4AF37] mb-1">Property Comparison</div>
                  <h3 className="font-serif text-[28px] leading-tight">Side-by-side analysis</h3>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-[#EDE6D3]/10 transition-colors" data-testid="compare-close">
                  <X size={20} />
                </button>
              </div>

              {/* Property Images & Names */}
              <div className="grid px-8 pt-8 pb-4" style={{ gridTemplateColumns: `200px repeat(${selected.length}, 1fr)` }}>
                <div />
                {selected.map((p) => (
                  <div key={p.id} className="px-3">
                    <div className="aspect-[4/3] overflow-hidden mb-3">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                    </div>
                    <h4 className="font-serif text-[18px] leading-tight">{p.title}</h4>
                  </div>
                ))}
              </div>

              {/* Comparison Table */}
              <div className="px-8 pb-8">
                {rows.map((row) => (
                  <div
                    key={row.key}
                    className="grid border-t border-[#EDE6D3]/10 py-4"
                    style={{ gridTemplateColumns: `200px repeat(${selected.length}, 1fr)` }}
                    data-testid={`compare-row-${row.key}`}
                  >
                    <div className="text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50 flex items-center gap-2">
                      {row.label}
                    </div>
                    {selected.map((p) => {
                      const val = row.fmt(p[row.key]);
                      const isHighlight = row.key === "price_inr" || row.key === "yield_pct";
                      return (
                        <div key={p.id} className={`px-3 text-[15px] ${isHighlight ? "font-serif text-[20px] text-[#D4AF37]" : "text-[#EDE6D3]/85"}`}>
                          {val}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
